export interface BrowserDataCacheSqlMigration {
  version: number;
  name: string;
  sql: string | string[];
}

export interface BrowserDataCacheSqlTable {
  name: string;
  createSql: string;
}

export interface BrowserDataCacheSqlView {
  name: string;
  createSql: string;
  dropSql?: string;
}

export interface BrowserDataCacheMaterializedView {
  name: string;
  tableName: string;
  createTableSql: string;
  refreshSql: string | string[];
  clearSql?: string;
}

export interface BrowserDataCacheSqlSchema {
  id: string;
  migrations?: BrowserDataCacheSqlMigration[];
  tables?: BrowserDataCacheSqlTable[];
  views?: BrowserDataCacheSqlView[];
  materializedViews?: BrowserDataCacheMaterializedView[];
}

export interface BrowserDataCacheSqlPlan {
  schemaId: string;
  migrations: BrowserDataCacheSqlMigration[];
  createSql: string[];
  viewSql: string[];
  materializedViewSql: string[];
  refreshMaterializedViewSql: string[];
}

export function buildSqlPlan(schema: BrowserDataCacheSqlSchema): BrowserDataCacheSqlPlan {
  const migrations = sortAndValidateMigrations(schema.migrations || []);
  const materializedViews = schema.materializedViews || [];
  return {
    schemaId: schema.id,
    migrations,
    createSql: [
      ...(schema.tables || []).map((table) => table.createSql),
      ...materializedViews.map((view) => view.createTableSql),
    ],
    viewSql: (schema.views || []).flatMap((view) => [
      view.dropSql || `DROP VIEW IF EXISTS ${quoteIdentifier(view.name)};`,
      view.createSql,
    ]),
    materializedViewSql: materializedViews.map((view) => view.createTableSql),
    refreshMaterializedViewSql: materializedViews.flatMap((view) => [
      view.clearSql || `DELETE FROM ${quoteIdentifier(view.tableName)};`,
      ...toSqlList(view.refreshSql),
    ]),
  };
}

export function sortAndValidateMigrations(migrations: BrowserDataCacheSqlMigration[]): BrowserDataCacheSqlMigration[] {
  const seen = new Set<number>();
  for (const migration of migrations || []) {
    if (!Number.isInteger(migration.version) || migration.version < 0) {
      throw new Error(`Invalid SQL migration version: ${migration.version}`);
    }
    if (seen.has(migration.version)) {
      throw new Error(`Duplicate SQL migration version: ${migration.version}`);
    }
    seen.add(migration.version);
  }
  return [...(migrations || [])].sort((a, b) => a.version - b.version);
}

function toSqlList(sql: string | string[]): string[] {
  return Array.isArray(sql) ? sql : [sql];
}

function quoteIdentifier(identifier: string): string {
  return `"${String(identifier || '').replace(/"/g, '""')}"`;
}
